import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import InquiryPopupForm from './InquiryPopupForm';
import serviceMenuItems, { flattenServiceMenuItems } from './serviceMenuItems';
import trainingMenuItems from './trainingMenuItems';

const ServicePageTemplate = ({
  seo,
  servicePath,
  relatedItems,
  relatedKicker,
  relatedTitle,
  relatedText,
  hero,
  trustStrip,
  capability,
  featureBreakdown,
  cta,
  ctaPlacement,
  videoDemo,
  videoDemoPlacement,
  audience,
  portfolio,
  contentSections,
  useCases,
  workflow,
  security,
  delivery,
  pricing,
  faq,
  reviews,
  finalCta,
  finalCtaPlacement,
  stickyMobileCta,
  inquiry,
  customActions = {},
  children,
}) => {
  const [isInquiryOpen, setIsInquiryOpen] = useState(false);
  const [currentInquirySubject, setCurrentInquirySubject] = useState(
    inquiry.defaultSubject
  );
  const [activeFaqIndex, setActiveFaqIndex] = useState(0);
  const [activeReviewIndex, setActiveReviewIndex] = useState(0);
  const [reviewTouchStart, setReviewTouchStart] = useState(null);
  const [reviewTouchEnd, setReviewTouchEnd] = useState(null);
  const [isStickyCtaVisible, setIsStickyCtaVisible] = useState(false);

  const openInquiry = (subjectOverride) => {
    setCurrentInquirySubject(subjectOverride || inquiry.defaultSubject);
    setIsInquiryOpen(true);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);

    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleAction = (action = {}) => {
    if (action.disabled) {
      return;
    }

    if (action.scrollToId) {
      scrollToSection(action.scrollToId);
      return;
    }

    if (action.url) {
      if (action.target === '_blank') {
        window.open(action.url, '_blank', 'noopener,noreferrer');
        return;
      }

      window.location.assign(action.url);
      return;
    }

    openInquiry(action.subject);
  };

  const handleConfiguredButton = (subject, actionName) => {
    if (actionName && typeof customActions[actionName] === 'function') {
      customActions[actionName]();
      return;
    }

    openInquiry(subject);
  };

  const getActionHref = (action = {}) => {
    if (action.scrollToId) {
      return `#${action.scrollToId}`;
    }

    return action.url || null;
  };

  const finalCtaSection = finalCta ? (
    <section
      id={finalCta.id}
      className={`chrome-service-cta chrome-service-final-cta ${
        finalCta.className || ''
      }`}
    >
      <div className="chrome-service-final-cta-band">
        <div className="auto-container">
          <div className="chrome-service-final-cta-panel">
            <div className="chrome-service-cta-copy">
              <span className="chrome-service-kicker">{finalCta.kicker}</span>
              <h2>{finalCta.title}</h2>
              <p>{finalCta.text}</p>
            </div>
            <div className="chrome-service-cta-buttons">
              {finalCta.primaryButtonUrl ? (
                <a
                  className="chrome-service-cta-button"
                  href={finalCta.primaryButtonUrl}
                  target={finalCta.primaryButtonTarget}
                  rel={finalCta.primaryButtonTarget === '_blank' ? 'noopener noreferrer' : undefined}
                >
                  <span className="chrome-service-cta-button-text">
                    {finalCta.primaryButtonLabel}
                  </span>
                  <span className="chrome-service-cta-button-icon fa fa-long-arrow-right"></span>
                </a>
              ) : (
                <button
                  type="button"
                  className="chrome-service-cta-button"
                  onClick={() => openInquiry(finalCta.primaryButtonSubject)}
                >
                  <span className="chrome-service-cta-button-text">
                    {finalCta.primaryButtonLabel}
                  </span>
                  <span className="chrome-service-cta-button-icon fa fa-long-arrow-right"></span>
                </button>
              )}
              {finalCta.secondaryButtonLabel ? (
                finalCta.secondaryButtonUrl ? (
                  <a
                    className="chrome-service-cta-button chrome-service-cta-button-secondary"
                    href={finalCta.secondaryButtonUrl}
                    target={finalCta.secondaryButtonTarget}
                    rel={finalCta.secondaryButtonTarget === '_blank' ? 'noopener noreferrer' : undefined}
                  >
                    <span className="chrome-service-cta-button-text">
                      {finalCta.secondaryButtonLabel}
                    </span>
                    <span className="chrome-service-cta-button-icon fa fa-calendar"></span>
                  </a>
                ) : (
                  <button
                    type="button"
                    className="chrome-service-cta-button chrome-service-cta-button-secondary"
                    onClick={() => openInquiry(finalCta.secondaryButtonSubject)}
                  >
                    <span className="chrome-service-cta-button-text">
                      {finalCta.secondaryButtonLabel}
                    </span>
                    <span className="chrome-service-cta-button-icon fa fa-calendar"></span>
                  </button>
                )
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  ) : null;

  const isFinalCtaBeforeExploreMore = finalCtaPlacement === 'beforeExploreMore';
  const isFinalCtaAfterWorkflow = finalCtaPlacement === 'afterWorkflow';
  const isCtaBeforeFeatureBreakdown = ctaPlacement === 'beforeFeatureBreakdown';
  const isCtaAfterAudience = ctaPlacement === 'afterAudience';
  const isCtaAfterUseCases = ctaPlacement === 'afterUseCases';
  const isVideoDemoBeforeDelivery = videoDemoPlacement === 'beforeDelivery';

  const renderCtaSection = () => (
    <section id={cta.id} className="chrome-service-cta chrome-service-cta-band">
      <div className="chrome-service-cta-shell">
        <div className="auto-container">
          <div className="chrome-service-cta-panel">
            <div className="chrome-service-cta-copy">
              <span className="chrome-service-kicker">{cta.kicker}</span>
              <h2>{cta.title}</h2>
              <p>{cta.text}</p>
            </div>
            <div className="chrome-service-cta-buttons">
              {cta.secondaryFirst ? (
                <>
                  {renderCtaSecondaryButton()}
                  {renderCtaPrimaryButton()}
                </>
              ) : (
                <>
                  {renderCtaPrimaryButton()}
                  {renderCtaSecondaryButton()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );

  const videoDemoSection = videoDemo ? (
    <section id={videoDemo.id} className="chrome-service-intro chrome-service-video-demo">
      <div className="auto-container">
        <div className="chrome-service-video-demo-card">
          <div className="chrome-service-video-demo-copy">
            <span className="chrome-service-kicker chrome-service-kicker-light">
              {videoDemo.kicker}
            </span>
            <h2>{videoDemo.title}</h2>
            <p>{videoDemo.text}</p>
          </div>
          <div className="chrome-service-video-demo-actions">
            {videoDemo.url ? (
              <a
                className={`chrome-service-cta-button chrome-service-cta-button-secondary ${
                  videoDemo.className || ''
                }`}
                href={videoDemo.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="chrome-service-cta-button-text">
                  {videoDemo.buttonLabel}
                </span>
                <span className="chrome-service-cta-button-icon fa fa-play"></span>
              </a>
            ) : videoDemo.buttonDisabled ? (
              <button
                type="button"
                className={`chrome-service-cta-button chrome-service-cta-button-secondary ${
                  videoDemo.className || ''
                }`}
                disabled
                aria-disabled="true"
              >
                <span className="chrome-service-cta-button-text">
                  {videoDemo.buttonLabel}
                </span>
                <span className="chrome-service-cta-button-icon fa fa-clock-o"></span>
              </button>
            ) : (
              <button
                type="button"
                className={`chrome-service-cta-button chrome-service-cta-button-secondary ${
                  videoDemo.className || ''
                }`}
                onClick={() => openInquiry(videoDemo.buttonSubject)}
              >
                <span className="chrome-service-cta-button-text">
                  {videoDemo.buttonLabel}
                </span>
                <span className="chrome-service-cta-button-icon fa fa-play"></span>
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  ) : null;

  useEffect(() => {
    // Warm image cache for first navigation to service pages.
    const preloadImage = new Image();
    preloadImage.src = hero.image;
  }, [hero.image]);

  useEffect(() => {
    if (!stickyMobileCta) {
      setIsStickyCtaVisible(false);
      return undefined;
    }

    const updateStickyCta = () => {
      const revealAfter = Math.max(window.innerHeight * 0.85, 520);
      setIsStickyCtaVisible(window.scrollY > revealAfter);
    };

    updateStickyCta();
    window.addEventListener('scroll', updateStickyCta, { passive: true });
    window.addEventListener('resize', updateStickyCta);

    return () => {
      window.removeEventListener('scroll', updateStickyCta);
      window.removeEventListener('resize', updateStickyCta);
    };
  }, [stickyMobileCta]);

  useEffect(() => {
    const reviewTimer = window.setInterval(() => {
      setActiveReviewIndex((current) => (current + 1) % reviews.items.length);
    }, 4200);

    return () => window.clearInterval(reviewTimer);
  }, [reviews.items.length]);

  const showPreviousReview = () => {
    setActiveReviewIndex((current) => (current === 0 ? reviews.items.length - 1 : current - 1));
  };

  const showNextReview = () => {
    setActiveReviewIndex((current) => (current + 1) % reviews.items.length);
  };

  const handleReviewTouchStart = (event) => {
    setReviewTouchEnd(null);
    setReviewTouchStart(event.targetTouches[0].clientX);
  };

  const handleReviewTouchMove = (event) => {
    setReviewTouchEnd(event.targetTouches[0].clientX);
  };

  const handleReviewTouchEnd = () => {
    if (reviewTouchStart === null || reviewTouchEnd === null) {
      return;
    }

    const swipeDistance = reviewTouchStart - reviewTouchEnd;

    if (swipeDistance > 50) {
      showNextReview();
    } else if (swipeDistance < -50) {
      showPreviousReview();
    }

    setReviewTouchStart(null);
    setReviewTouchEnd(null);
  };

  const normalizeRelatedPath = (path = '') => {
    const cleanPath = path.split('#')[0].split('?')[0].replace(/\/+$/, '');
    return cleanPath || '/';
  };

  const normalizedServicePath = normalizeRelatedPath(servicePath);
  const isTrainingPage = normalizedServicePath.startsWith('/training/');
  const sourceRelatedItems =
    relatedItems ||
    (isTrainingPage ? trainingMenuItems : flattenServiceMenuItems(serviceMenuItems));
  const allServiceLinks = sourceRelatedItems.filter(
    (item, index, array) =>
      normalizeRelatedPath(item.to) !== normalizedServicePath &&
      array.findIndex(
        (entry) => normalizeRelatedPath(entry.to) === normalizeRelatedPath(item.to)
      ) === index
  );

  const siteOrigin = 'https://www.swiftmazetech.com';
  const selectedOgImage = seo.ogImage || hero.image;
  const ogImage = selectedOgImage?.startsWith('http')
    ? selectedOgImage
    : `${siteOrigin}${selectedOgImage}`;
  const ogType = seo.ogType || 'website';

  const renderCtaPrimaryButton = () =>
    cta.buttonUrl ? (
      <a
        className="chrome-service-cta-button"
        href={cta.buttonUrl}
        target={cta.buttonTarget}
        rel={cta.buttonTarget === '_blank' ? 'noopener noreferrer' : undefined}
      >
        <span className="chrome-service-cta-button-text">
          {cta.buttonLabel}
        </span>
        <span className={`chrome-service-cta-button-icon fa ${cta.buttonIcon || 'fa-long-arrow-right'}`}></span>
      </a>
    ) : (
      <button
        type="button"
        className="chrome-service-cta-button"
        onClick={() => handleConfiguredButton(cta.buttonSubject, cta.buttonAction)}
      >
        <span className="chrome-service-cta-button-text">
          {cta.buttonLabel}
        </span>
        <span className={`chrome-service-cta-button-icon fa ${cta.buttonIcon || 'fa-long-arrow-right'}`}></span>
      </button>
    );

  const renderCtaSecondaryButton = () =>
    cta.secondaryButtonLabel ? (
      cta.secondaryButtonUrl ? (
        <a
          className="chrome-service-cta-button chrome-service-cta-button-secondary"
          href={cta.secondaryButtonUrl}
          target={cta.secondaryButtonTarget}
          rel={cta.secondaryButtonTarget === '_blank' ? 'noopener noreferrer' : undefined}
        >
          <span className="chrome-service-cta-button-text">
            {cta.secondaryButtonLabel}
          </span>
          <span className={`chrome-service-cta-button-icon fa ${cta.secondaryButtonIcon || 'fa-calendar'}`}></span>
        </a>
      ) : (
        <button
          type="button"
          className="chrome-service-cta-button chrome-service-cta-button-secondary"
          onClick={() =>
            handleConfiguredButton(
              cta.secondaryButtonSubject,
              cta.secondaryButtonAction
            )
          }
        >
          <span className="chrome-service-cta-button-text">
            {cta.secondaryButtonLabel}
          </span>
          <span className={`chrome-service-cta-button-icon fa ${cta.secondaryButtonIcon || 'fa-calendar'}`}></span>
        </button>
      )
    ) : null;

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: seo.schemaName || hero.title,
    description: seo.description,
    url: seo.canonical,
    publisher: {
      '@type': 'Organization',
      name: 'SwiftMaze Technology',
      url: siteOrigin,
    },
  };

  return (
    <>
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href={seo.canonical} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:type" content={ogType} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={seo.canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="preload" as="image" href={hero.image} fetchpriority="high" />
        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
      </Helmet>

      <section className="chrome-service-hero">
        <img
          className="chrome-service-hero-bg"
          src={hero.image}
          alt=""
          loading="eager"
          fetchpriority="high"
          decoding="sync"
          aria-hidden="true"
        />
        <div className="auto-container">
          <div className="chrome-service-hero-panel">
            <div className="chrome-service-hero-copy">
              <span className="chrome-service-kicker">{hero.kicker}</span>
              <h1>{hero.title}</h1>
              <p className="chrome-service-lead">{hero.lead}</p>
              <div className="chrome-service-actions">
                {hero.actionButtons?.length ? (
                  hero.actionButtons.map((action) => {
                    const actionClassName = `theme-btn chrome-service-hero-action-button ${
                      action.icon ? 'has-icon' : ''
                    } ${
                      action.variant === 'ghost'
                        ? 'chrome-service-hero-ghost-button'
                        : 'btn-style-one'
                    } ${action.className || ''}`;

                    const actionContent = (
                      <>
                        {action.icon ? (
                          <span
                            className={`chrome-service-hero-action-icon fa ${action.icon}`}
                            aria-hidden="true"
                          ></span>
                        ) : null}
                        <span className="txt">{action.label}</span>
                      </>
                    );

                    return action.disabled ? (
                      <button
                        key={action.label}
                        type="button"
                        className={actionClassName}
                        disabled
                        aria-disabled="true"
                      >
                        {actionContent}
                      </button>
                    ) : (
                      <a
                        key={action.label}
                        className={actionClassName}
                        href={getActionHref(action) || '#'}
                        target={action.target}
                        rel={action.target === '_blank' ? 'noopener noreferrer' : undefined}
                        onClick={(event) => {
                          if (action.scrollToId || !action.url) {
                            event.preventDefault();
                            handleAction(action);
                          }
                        }}
                      >
                        {actionContent}
                      </a>
                    );
                  })
                ) : (
                  <>
                    {hero.buttonUrl ? (
                      <a
                        className="theme-btn btn-style-one"
                        href={hero.buttonUrl}
                        target={hero.buttonTarget}
                        rel={hero.buttonTarget === '_blank' ? 'noopener noreferrer' : undefined}
                      >
                        <span className="txt">{hero.buttonLabel}</span>
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="theme-btn btn-style-one"
                        onClick={() =>
                          handleAction({
                            label: hero.buttonLabel,
                            subject: hero.buttonSubject,
                          })
                        }
                      >
                        <span className="txt">{hero.buttonLabel}</span>
                      </button>
                    )}
                    {hero.secondaryButtonLabel ? (
                      <button
                        type="button"
                        className="chrome-service-text-link"
                        onClick={() =>
                          handleAction({
                            label: hero.secondaryButtonLabel,
                            subject: hero.secondaryButtonSubject,
                          })
                        }
                      >
                        {hero.secondaryButtonLabel}
                      </button>
                    ) : null}
                  </>
                )}
              </div>
              {hero.chips?.length ? (
                <div className="chrome-service-chip-row">
                  {hero.chips.map((chip) => (
                    <span key={chip}>{chip}</span>
                  ))}
                </div>
              ) : null}
              {trustStrip?.items?.length ? (
                <div className="chrome-service-trust-strip">
                  {trustStrip.items.map((item) => (
                    <div className="chrome-service-trust-item" key={item.label}>
                      <strong>{item.value}</strong>
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      <section id={capability.id} className="chrome-service-intro chrome-service-capability-section">
        <div className="auto-container">
          <div className="chrome-service-heading centered">
            <span className="chrome-service-kicker chrome-service-kicker-light">
              {capability.kicker}
            </span>
            <h2>{capability.title}</h2>
            <p>{capability.text}</p>
          </div>

          <div className="chrome-service-capability-grid">
            {capability.items.map((item) => (
              <article className="chrome-service-capability-card" key={item.title}>
                <div className="chrome-service-card-icon">
                  <span className={`fa ${item.icon}`}></span>
                </div>
                <div className="chrome-service-card-copy">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {portfolio?.items?.length ? (
        <section id={portfolio.id} className="chrome-service-oman-work">
          <div className="auto-container">
            <div className="chrome-service-heading centered chrome-service-heading-narrow">
              <span className="chrome-service-kicker chrome-service-kicker-light">{portfolio.kicker}</span>
              <h2>{portfolio.title}</h2>
              <p>{portfolio.text}</p>
            </div>
            <div className="chrome-service-oman-work-grid">
              {portfolio.items.map((item) => (
                <article className="chrome-service-oman-work-card" key={item.name}>
                  <div className={`chrome-service-oman-work-brand chrome-service-oman-work-brand--${item.type}`}>
                    <img src={item.logo} alt={`${item.name} logo`} loading="lazy" />
                  </div>
                  <div className="chrome-service-oman-work-copy">
                    <span className="chrome-service-oman-work-sector">{item.sector}</span>
                    <h3>{item.name}</h3>
                    <p>{item.description}</p>
                    <ul>
                      {item.highlights.map((highlight) => (
                        <li key={highlight}><span className="fa fa-check" aria-hidden="true"></span>{highlight}</li>
                      ))}
                    </ul>
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="chrome-service-oman-work-link">
                      Visit Website <span className="fa fa-external-link" aria-hidden="true"></span>
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {children}

      {isCtaBeforeFeatureBreakdown ? renderCtaSection() : null}

      {featureBreakdown?.items?.length ? (
        <section
          id={featureBreakdown.id}
          className="chrome-service-intro chrome-service-feature-breakdown"
        >
          <div className="auto-container">
            <div className="chrome-service-heading centered">
              <span className="chrome-service-kicker chrome-service-kicker-light">
                {featureBreakdown.kicker}
              </span>
              <h2>{featureBreakdown.title}</h2>
              <p>{featureBreakdown.text}</p>
            </div>

            <div className="chrome-service-feature-grid">
              {featureBreakdown.items.map((item) => (
                <article className="chrome-service-feature-card" key={item.title}>
                  <div className="chrome-service-card-icon">
                    <span className={`fa ${item.icon}`}></span>
                  </div>
                  <div className="chrome-service-card-copy">
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {!isCtaBeforeFeatureBreakdown && !isCtaAfterAudience && !isCtaAfterUseCases
        ? renderCtaSection()
        : null}

      <section id={audience.id} className="chrome-service-split">
        <div className="auto-container">
          <div className="chrome-service-split-grid">
            <div className="chrome-service-split-media">
              <div className="chrome-service-media-frame">
                <img
                  src={audience.image}
                  alt={audience.imageAlt}
                  style={audience.imageStyle}
                />
              </div>
            </div>

            <div className="chrome-service-split-copy">
              <span className="chrome-service-kicker chrome-service-kicker-light">
                {audience.kicker}
              </span>
              <h2>{audience.title}</h2>
              <p>{audience.text}</p>

              <div className="chrome-service-audience-list">
                {audience.items.map((item) => (
                  <div className="chrome-service-audience-row" key={item.title}>
                    <div className="chrome-service-audience-icon">
                      <span className={`fa ${item.icon}`}></span>
                    </div>
                    <div className="chrome-service-audience-copy">
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {isCtaAfterAudience ? renderCtaSection() : null}

      {contentSections?.map((contentSection, sectionIndex) => (
        <section
          id={contentSection.id}
          className={`chrome-service-editorial ${
            sectionIndex % 2 === 1 ? 'chrome-service-editorial-alt' : ''
          }`}
          key={contentSection.id || contentSection.title}
        >
          <div className="auto-container">
            <div className={`chrome-service-editorial-grid ${
              sectionIndex % 2 === 1 ? 'chrome-service-editorial-grid-reverse' : ''
            }`}>
              <div className="chrome-service-editorial-copy">
                <span className="chrome-service-kicker chrome-service-kicker-light">
                  {contentSection.kicker}
                </span>
                <h2>{contentSection.title}</h2>
                {contentSection.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>

              <div className="chrome-service-editorial-panel">
                {contentSection.items.map((item, itemIndex) => (
                  <article className="chrome-service-editorial-item" key={item.title}>
                    <span className="chrome-service-editorial-index">
                      {`${itemIndex + 1}`.padStart(2, '0')}
                    </span>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {useCases?.items?.length ? (
        <section id={useCases.id} className="chrome-service-intro chrome-service-usecases">
          <div className="auto-container">
            <div className="chrome-service-heading centered chrome-service-heading-narrow">
              <span className="chrome-service-kicker chrome-service-kicker-light">
                {useCases.kicker}
              </span>
              <h2>{useCases.title}</h2>
              <p>{useCases.text}</p>
            </div>

            <div className="chrome-service-usecase-grid">
              {useCases.items.map((item) => (
                <article className="chrome-service-usecase-card" key={item.title}>
                  <div className="chrome-service-card-icon">
                    <span className={`fa ${item.icon}`}></span>
                  </div>
                  <div className="chrome-service-card-copy">
                    <h3>{item.title}</h3>
                    <p>{item.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {isCtaAfterUseCases ? renderCtaSection() : null}

      {workflow?.items?.length ? (
        <section id={workflow.id} className="chrome-service-delivery chrome-service-workflow">
          <div className="auto-container">
            <div className="chrome-service-heading centered chrome-service-heading-narrow">
              <span className="chrome-service-kicker chrome-service-kicker-light">
                {workflow.kicker}
              </span>
              <h2>{workflow.title}</h2>
              <p>{workflow.text}</p>
            </div>

            <div
              className={`chrome-service-workflow-steps ${
                workflow.items.length === 5 ? 'chrome-service-workflow-steps-five' : ''
              }`}
            >
              {workflow.items.map((item, index) => (
                <article className="chrome-service-workflow-card" key={item.title}>
                  <span className="chrome-service-delivery-number">
                    {`${index + 1}`.padStart(2, '0')}
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {isFinalCtaAfterWorkflow ? finalCtaSection : null}

      {security?.items?.length ? (
        <section id={security.id} className="chrome-service-split chrome-service-security">
          <div className="auto-container">
            <div className="chrome-service-split-grid chrome-service-split-grid-reverse">
              <div className="chrome-service-split-copy">
                <span className="chrome-service-kicker chrome-service-kicker-light">
                  {security.kicker}
                </span>
                <h2>{security.title}</h2>
                <p>{security.text}</p>

                <div className="chrome-service-audience-list chrome-service-security-list">
                  {security.items.map((item) => (
                    <div className="chrome-service-audience-row" key={item.title}>
                      <div className="chrome-service-audience-icon">
                        <span className={`fa ${item.icon}`}></span>
                      </div>
                      <div className="chrome-service-audience-copy">
                        <h3>{item.title}</h3>
                        <p>{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="chrome-service-split-media">
                <div className="chrome-service-security-panel">
                  {security.highlights?.map((item) => (
                    <article className="chrome-service-security-highlight" key={item.title}>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {isVideoDemoBeforeDelivery ? videoDemoSection : null}

      <section id={delivery.id} className="chrome-service-delivery">
        <div className="auto-container">
          <div className="chrome-service-heading centered chrome-service-heading-narrow">
            <span className="chrome-service-kicker chrome-service-kicker-light">
              {delivery.kicker}
            </span>
            <h2>{delivery.title}</h2>
            <p>{delivery.text}</p>
          </div>

          <div className="chrome-service-delivery-grid">
            {delivery.items.map((item) => (
              <article className="chrome-service-delivery-card" key={item.number}>
                <span className="chrome-service-delivery-number">{item.number}</span>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {!isVideoDemoBeforeDelivery ? videoDemoSection : null}

      {pricing?.plans?.length ? (
        <section
          id="service-pricing"
          className="chrome-service-intro chrome-service-pricing"
        >
          <div className="auto-container">
            <div className="chrome-service-heading centered chrome-service-heading-narrow">
              <span className="chrome-service-kicker chrome-service-kicker-light">
                {pricing.kicker}
              </span>
              <h2>{pricing.title}</h2>
              <p>{pricing.text}</p>
            </div>

            <div className="chrome-service-pricing-grid">
              {pricing.plans.map((plan) => (
                <article
                  className={`chrome-service-pricing-card ${
                    plan.featured ? 'is-featured' : ''
                  }`}
                  key={plan.name}
                >
                  <div className="chrome-service-pricing-head">
                    {plan.badge ? (
                      <span className="chrome-service-pricing-badge">{plan.badge}</span>
                    ) : null}
                    <h3>
                      {plan.name}
                      {plan.value ? (
                        <span className="chrome-service-pricing-value-inline">
                          {plan.value}
                        </span>
                      ) : null}
                    </h3>
                    {plan.caption ? (
                      <div className="chrome-service-pricing-caption">
                        {plan.caption}
                      </div>
                    ) : null}
                    <p>{plan.summary}</p>
                  </div>
                  {plan.metrics?.length ? (
                    <div className="chrome-service-pricing-metrics">
                      {plan.metrics.map((metric) => (
                        <div className="chrome-service-pricing-metric" key={metric.label}>
                          <strong>{metric.value}</strong>
                          <span>{metric.label}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                    {plan.buttonHref ? (
                      <a
                        className="chrome-service-cta-button chrome-service-cta-button-secondary"
                        href={plan.buttonHref}
                        target={plan.buttonTarget}
                        rel={plan.buttonTarget === '_blank' ? 'noopener noreferrer' : undefined}
                      >
                        <span className="chrome-service-cta-button-text">
                          {plan.buttonLabel || 'Contact Sales'}
                        </span>
                        <span className="chrome-service-cta-button-icon fa fa-angle-right"></span>
                      </a>
                    ) : (
                      <button
                        type="button"
                        className="chrome-service-cta-button chrome-service-cta-button-secondary"
                        onClick={() => openInquiry(plan.buttonSubject)}
                      >
                        <span className="chrome-service-cta-button-text">
                          {plan.buttonLabel || 'Contact Sales'}
                        </span>
                        <span className="chrome-service-cta-button-icon fa fa-angle-right"></span>
                      </button>
                    )}
                  <ul className="chrome-service-pricing-list">
                    {plan.items.map((item) => (
                      <li key={item}>
                        <span className="fa fa-check-circle"></span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  {plan.detailGroups?.length ? (
                    <div className="chrome-service-pricing-detail-groups">
                      {plan.detailGroups.map((group) => (
                        <div
                          className="chrome-service-pricing-detail-group"
                          key={group.title}
                        >
                          <div className="chrome-service-pricing-detail-head">
                            <h4>{group.title}</h4>
                            {group.count ? (
                              <span className="chrome-service-pricing-detail-count">
                                {group.count}
                              </span>
                            ) : null}
                          </div>
                          <ul>
                            {group.items.map((item) => (
                              <li key={item.label}>
                                <span>{item.label}</span>
                                <strong>{item.value}</strong>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {plan.note ? (
                    <p className="chrome-service-pricing-note">{plan.note}</p>
                  ) : null}
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      {isFinalCtaBeforeExploreMore ? finalCtaSection : null}

      <section className="chrome-service-other-services">
        <div className="auto-container">
          <div className="chrome-service-heading centered chrome-service-heading-narrow chrome-service-other-head">
            <span className="chrome-service-kicker chrome-service-kicker-light">
              {relatedKicker || 'Explore More'}
            </span>
            <h2>{relatedTitle || (isTrainingPage ? 'Related Training Programs' : 'Related Services')}</h2>
            <p>
              {relatedText ||
                (isTrainingPage
                  ? 'Explore other training, internship, and project guidance options from SwiftMaze Tech.'
                  : 'Explore more services if you need complete support across web, software, marketing, mobile apps, CRM, ERP, QA, and business systems.')}
            </p>
          </div>

          <div className="chrome-service-other-grid">
            {allServiceLinks.map((item) => (
              <Link className="chrome-service-other-card" key={item.to} to={item.to}>
                <span>{item.label}</span>
                <span className="fa fa-angle-right"></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="chrome-service-proof">
        <div className="auto-container">
          <div className="chrome-service-proof-grid">
            <div className="chrome-service-proof-faq">
              <div className="chrome-service-proof-head">
                <span className="chrome-service-kicker chrome-service-kicker-light">
                  {faq.kicker}
                </span>
                <h2>{faq.title}</h2>
                <p>{faq.text}</p>
              </div>

              <div className="chrome-service-faq-list">
                {faq.items.map((item, index) => {
                  const isActive = activeFaqIndex === index;

                  return (
                    <article
                      className={`chrome-service-faq-item ${isActive ? 'is-active' : ''}`}
                      key={item.question}
                    >
                      <button
                        type="button"
                        className="chrome-service-faq-button"
                        aria-expanded={isActive}
                        onClick={() => setActiveFaqIndex(isActive ? -1 : index)}
                      >
                        <span>{item.question}</span>
                        <span
                          className={`chrome-service-faq-icon fa ${
                            isActive ? 'fa-minus' : 'fa-plus'
                          }`}
                        ></span>
                      </button>
                      <div className={`chrome-service-faq-panel ${isActive ? 'is-open' : ''}`}>
                        <p>{item.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="chrome-service-proof-review">
              <div className="chrome-service-proof-head">
                <span className="chrome-service-kicker chrome-service-kicker-light">
                  {reviews.kicker}
                </span>
                <h2>{reviews.title}</h2>
                <p>{reviews.text}</p>
              </div>

              <div className="chrome-service-review-slider">
                <div className="chrome-service-review-stage">
                  <button
                    type="button"
                    className="chrome-service-review-arrow"
                    onClick={showPreviousReview}
                    aria-label="Show previous review"
                  >
                    <span className="fa fa-angle-left"></span>
                  </button>

                  <div
                    className="chrome-service-review-viewport"
                    onTouchStart={handleReviewTouchStart}
                    onTouchMove={handleReviewTouchMove}
                    onTouchEnd={handleReviewTouchEnd}
                  >
                    <article
                      className="chrome-service-review-card chrome-service-review-card-active"
                      key={reviews.items[activeReviewIndex].name}
                    >
                      {!reviews.hideQuote ? (
                        <span className="chrome-service-review-quote">"</span>
                      ) : null}
                      <div className="chrome-service-review-stars">
                        {Array.from({ length: reviews.items[activeReviewIndex].rating }).map((_, starIndex) => (
                          <span
                            className="fa fa-star"
                            key={`${reviews.items[activeReviewIndex].name}-${starIndex}`}
                          ></span>
                        ))}
                      </div>
                      <p className="chrome-service-review-message">
                        {reviews.items[activeReviewIndex].message}
                      </p>
                      <div className="chrome-service-review-meta">
                        <h3>{reviews.items[activeReviewIndex].name}</h3>
                        <span>{reviews.items[activeReviewIndex].position}</span>
                      </div>
                    </article>
                  </div>

                  <button
                    type="button"
                    className="chrome-service-review-arrow"
                    onClick={showNextReview}
                    aria-label="Show next review"
                  >
                    <span className="fa fa-angle-right"></span>
                  </button>
                </div>

                <div className="chrome-service-review-dots">
                  {reviews.items.map((item, index) => (
                    <button
                      type="button"
                      key={item.name}
                      className={`chrome-service-review-dot ${
                        activeReviewIndex === index ? 'is-active' : ''
                      }`}
                      onClick={() => setActiveReviewIndex(index)}
                      aria-label={`Show review ${index + 1}`}
                    ></button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {!isFinalCtaBeforeExploreMore && !isFinalCtaAfterWorkflow ? finalCtaSection : null}

      {stickyMobileCta && isStickyCtaVisible ? (
        <div className="chrome-service-sticky-mobile">
          {stickyMobileCta.primaryButtonUrl ? (
            <a
              className="chrome-service-sticky-main"
              href={stickyMobileCta.primaryButtonUrl}
              target={stickyMobileCta.primaryButtonTarget}
              rel={stickyMobileCta.primaryButtonTarget === '_blank' ? 'noopener noreferrer' : undefined}
            >
              {stickyMobileCta.primaryButtonLabel}
            </a>
          ) : (
            <button
              type="button"
              className="chrome-service-sticky-main"
              onClick={() => openInquiry(stickyMobileCta.primaryButtonSubject)}
            >
              {stickyMobileCta.primaryButtonLabel}
            </button>
          )}
          {stickyMobileCta.secondaryButtonLabel ? (
            stickyMobileCta.secondaryButtonUrl ? (
              <a
                className="chrome-service-sticky-secondary"
                href={stickyMobileCta.secondaryButtonUrl}
                target={stickyMobileCta.secondaryButtonTarget}
                rel={stickyMobileCta.secondaryButtonTarget === '_blank' ? 'noopener noreferrer' : undefined}
              >
                {stickyMobileCta.secondaryButtonLabel}
              </a>
            ) : (
              <button
                type="button"
                className="chrome-service-sticky-secondary"
                onClick={() => openInquiry(stickyMobileCta.secondaryButtonSubject)}
              >
                {stickyMobileCta.secondaryButtonLabel}
              </button>
            )
          ) : null}
        </div>
      ) : null}

      <InquiryPopupForm
        isOpen={isInquiryOpen}
        onClose={() => setIsInquiryOpen(false)}
        defaultSubject={currentInquirySubject}
        formTitle={inquiry.formTitle}
        formSubtitle={inquiry.formSubtitle}
        trackingFormName={inquiry.trackingFormName}
        formVariant={inquiry.formVariant || (isTrainingPage ? 'training' : 'default')}
        defaultCountryCode={inquiry.defaultCountryCode}
      />
    </>
  );
};

export default ServicePageTemplate;
